import IFilterInputs from "./types/filter-inputs";
import IFilterOptions from "./types/filter-options";
import JobOffer from "./job-offer";

type FilterOptions = IFilterOptions["departments"] | IFilterOptions["places"] | IFilterOptions['contracts'] | IFilterOptions['terminability'];

export default class Filter {
  public onchange?: Function;

  private _department = "";
  private _place = "";
  private _contract = "";
  private _terminability = "";

  constructor(
    private _filterInputs: IFilterInputs,
    private _offers: JobOffer[]
  ) {}

  public initialize(filterOptions: IFilterOptions): void {
    if (filterOptions.departments.size <= 0) {
      const parent = this._filterInputs.department.parentNode;
      if (parent)
        parent.parentNode?.removeChild(parent);
    } else {
      this._populateDepartments(filterOptions.departments);
    }
    this._populatePlaces(filterOptions.places);
    this._populateTerminability(filterOptions.terminability);

    this._registerEventListeners();
  }

  private _populateFilter(
    filterOptions: FilterOptions,
    firstTextContent: string,
    selectElement: HTMLSelectElement
  ): void {
    selectElement.innerHTML = "";

    const emptyOption = document.createElement("option");
    emptyOption.value = "";
    emptyOption.textContent = firstTextContent;
    selectElement.appendChild(emptyOption);

    const filterOptionsArray = Array.from(filterOptions);
    filterOptionsArray.sort((a, b) => a.localeCompare(b));

    filterOptionsArray.forEach(option => {
      const element = document.createElement("option");
      element.value = option;
      element.textContent = option;
      selectElement.appendChild(element);
    });
  }

  private _populateDepartments(
    departmentOptions: IFilterOptions["departments"]
  ): void {
    this._populateFilter(
      departmentOptions,
      "– Stellenarten –",
      this._filterInputs.department
    );
  }

  private _populatePlaces(placeOptions: IFilterOptions["places"]): void {
    this._populateFilter(
      placeOptions,
      "– Standorte –",
      this._filterInputs.place
    );
  }

  private _populateTerminability(terminabilityOptions: IFilterOptions["terminability"]): void {
    if (this._filterInputs.terminability) {
      this._populateFilter(
        terminabilityOptions,
        "- Fachgebiet -",
        this._filterInputs.terminability
      );
    }
  }

  private _registerEventListeners(): void {
    this._filterInputs.department.addEventListener("change", () =>
      this._handleDepartmentChange()
    );

    this._filterInputs.place.addEventListener("change", () =>
      this._handlePlaceChange()
    );

    this._filterInputs.terminability?.addEventListener("change", () => {
      this._handleTerminabilityChange();
    });
  }

  private _handleDepartmentChange(): void {
    this._department = this._filterInputs.department.value;
    this._updateFilter();
  }

  private _handlePlaceChange(): void {
    this._place = this._filterInputs.place.value;
    this._updateFilter();
  }

  private _handleTerminabilityChange(): void {
    this._terminability = this._filterInputs.terminability?.value || '';
    this._updateFilter();
  }

  private _updateMutEx(): void {
    const availableDepartments = this._offers
      .filter(offer =>
        (!this._place || offer.places.includes(this._place))
        && (!this._terminability || offer.terminability.includes(this._terminability))
      )
      .map(offer => offer.department);

    const availablePlaces: string[] = [];
    this._offers
      .filter(offer =>
        (!this._department || offer.department === this._department)
        && (!this._terminability || offer.terminability === this._terminability)
      )
      .forEach(offer => availablePlaces.push(...offer.places));

    const availableTerminability: string[] =
      this._offers
        .filter(offer =>
          (!this._place || offer.places.includes(this._place))
          && (!this._department || offer.department === this._department)
        )
        .map(offer => offer.terminability);

    Array.from(this._filterInputs.department.options).forEach(
      option =>
        (option.disabled =
          !availableDepartments.includes(option.value) && !!option.value)
    );

    if (this._filterInputs.terminability) {
      Array.from(this._filterInputs.terminability.options).forEach(
        option =>
          (option.disabled =
            !availableTerminability.includes(option.value) && !!option.value)
      );
    }

    Array.from(this._filterInputs.place.options).forEach(
      option =>
        (option.disabled =
          !availablePlaces.includes(option.value) && !!option.value)
    );
  }

  private _updateFilter(): void {
    if (this.onchange) {
      this.onchange();
    }

    requestAnimationFrame(() => {
      this._offers.forEach(offer => {
        if (this._department && offer.department !== this._department) {
          offer.hide();
          return;
        }

        if (this._contract && offer.contract !== this._contract) {
          offer.hide();
          return;
        }

        if (this._terminability && offer.terminability !== this._terminability) {
          offer.hide();
          return;
        }

        if (this._place && !offer.places.includes(this._place)) {
          offer.hide();
          return;
        }

        offer.show();
      });

      this._updateMutEx();
    });
  }
}
